package org.zest.insan_360_website.utils

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.fetch.ResponseInit
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState

object NetworkUtils {
    private val isLocalDev = window.location.hostname == "localhost" || window.location.hostname == "127.0.0.1"
    private const val ENABLE_DEV_ACTIONS = false

    fun devInterceptor(endpoint: String, method: String, payload: String? = null): Response? {
        if (!isLocalDev || !ENABLE_DEV_ACTIONS) return null

        if (payload != null) console.log("📦 [DEV] Payload: $payload")

        if (endpoint == "/api/auth/login" && method == "POST") {
            console.warn("🛠️ [DEV] Faking Login Response!")
            console.warn("🛠️ [DEV] $method to $endpoint")

            val fakeJsonResponse = """
            {
                "success": true,
                "message": "Dev mock login successful",
                "data": {
                    "token": "fake-dev-jwt-token-12345",
                    "name": "fakeDevAccount",
                    "email": "dev@insan360.com",
                    "role": "STUDENT"
                }
            }
        """.trimIndent()

            return Response(fakeJsonResponse, ResponseInit(status = 200, statusText = "OK"))
        }

        return null
    }

    suspend inline fun <reified T> runSecureAction(crossinline networkBlock: suspend () -> T): T? {
        if (AuthState.isTokenExpired) {
            val revived = AuthState.silentRefresh()
            if (!revived) return null
        }

        return try {
            val initialResult = networkBlock()
            if (initialResult is Response) {
                if (initialResult.status == 401.toShort()) {
                    console.warn("401 Unauthorized intercepted. Attempting silent refresh...")
                    val revived = AuthState.silentRefresh()
                    return if (revived) {
                        networkBlock()
                    } else {
                        initialResult
                    }
                }
            }
            initialResult

        } catch (e: Throwable) {
            val errorContext = e.message ?: ""
            if (errorContext.contains("401") || errorContext.contains("Unauthorized")) {
                console.warn("401 Exception caught. Attempting silent refresh...")
                val revived = AuthState.silentRefresh()

                return if (revived) {
                    try {
                        networkBlock()
                    } catch (_: Throwable) {
                        throw Exception(
                            LanguageSettings.General.networkFailure.localized
                        )
                    }
                } else null
            } else {
                console.error("Network Fetch Exception: ${e.message}")
                throw Exception(
                    LanguageSettings.General.networkFailure.localized
                )
            }
        }
    }

    suspend inline fun <reified Req> postSecureApi(endpoint: String, requestPayload: Req): Response? {

        val payloadString = Json.encodeToString(requestPayload)

        devInterceptor(endpoint, "POST", payloadString)?.let { return it }

        return runSecureAction {
            val headers = Headers().apply {
                append("Authorization", "Bearer ${AuthState.token}")
                append("Content-Type", "application/json")
            }

            val requestInit = RequestInit(
                method = "POST",
                headers = headers,
                body = payloadString
            )

            window.fetch(endpoint, requestInit).await()
        }
    }

    suspend inline fun postSecureApi(endpoint: String): Response? {

        devInterceptor(endpoint, "POST")?.let { return it }

        return runSecureAction {
            val headers = Headers().apply {
                append("Authorization", "Bearer ${AuthState.token}")
            }
            val requestInit = RequestInit(
                method = "POST",
                headers = headers
            )
            window.fetch(endpoint, requestInit).await()
        }
    }

    suspend inline fun postApi(endpoint: String): Response {

        devInterceptor(endpoint, "POST")?.let { return it }

        val headers = Headers().apply {
            append("Authorization", "Bearer ${AuthState.token}")
        }
        val requestInit = RequestInit(
            method = "POST",
            headers = headers
        )
        return window.fetch(endpoint, requestInit).await()
    }

    suspend inline fun <reified Req> postBasicApi(endpoint: String, requestPayload: Req): Response {
        val payloadString = Json.encodeToString(requestPayload)

        devInterceptor(endpoint, "POST", payloadString)?.let { return it }

        val headers = Headers().apply {
            append("Content-Type", "application/json")
            append("ngrok-skip-browser-warning", "true")
        }

        val requestInit = RequestInit(
            method = "POST",
            headers = headers,
            body = payloadString
        )

        return window.fetch(endpoint, requestInit).await()
    }

    suspend inline fun <reified Req> patchSecureApi(endpoint: String, requestPayload: Req): Response? {

        val payloadString = Json.encodeToString(requestPayload)

        devInterceptor(endpoint, "PATCH", payloadString)?.let { return it }

        return runSecureAction {
            val headers = Headers().apply {
                append("Authorization", "Bearer ${AuthState.token}")
                append("Content-Type", "application/json")
            }
            val requestInit = RequestInit(
                method = "PATCH",
                headers = headers,
                body = payloadString
            )
            window.fetch(endpoint, requestInit).await()
        }
    }

    suspend inline fun patchSecureApi(endpoint: String): Response? {

        devInterceptor(endpoint, "PATCH")?.let { return it }

        return runSecureAction {
            val headers = Headers().apply {
                append("Authorization", "Bearer ${AuthState.token}")
                append("Content-Type", "application/json")
            }
            val requestInit = RequestInit(
                method = "PATCH",
                headers = headers,
            )
            window.fetch(endpoint, requestInit).await()
        }
    }

    suspend inline fun getSecureApi(endpoint: String): Response? {

        devInterceptor(endpoint, "GET")?.let { return it }

        return runSecureAction {
            val headers = Headers().apply {
                append("Authorization", "Bearer ${AuthState.token}")
            }
            val requestInit = RequestInit(
                method = "GET",
                headers = headers
            )
            window.fetch(endpoint, requestInit).await()
        }
    }
}